import {
  ChangeEvent,
  useEffect,
  useState,
  MouseEvent as ReactMouseEvent,
  FocusEvent,
} from "react"

import { ApiError, ErrorsMap } from "api/http/utils"
import { ButtonBase, Tooltip } from "@material-ui/core"
import { MediaConfigScope, MediaType } from "../types"
import { css, cx } from "@emotion/css"
import { queryCache, useMutation } from "react-query"

import { Switch } from "@material-ui/core"
import { TextInput } from "components/inputs/TextInput"
import { WithAuthority } from "components/WithAuthority"
import { patchMediaType } from "api/globalMediaTypes"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"

type Props = {
  colorOptions: string[]
  mediaType: MediaType
  handleChange: (
    evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void
  handleBlur?: (
    evt: FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLButtonElement>,
  ) => void
  handleColorSelection: (
    e: ReactMouseEvent<HTMLButtonElement, MouseEvent>,
    color?: string | undefined,
  ) => void
  configOwnerId?: string
  configScope?: MediaConfigScope
  updatedAt: number
}

export default function GeneralInfo(props: Props) {
  const { t } = useI18n()
  const { hasAuthority } = useAuthenticated()
  const {
    colorOptions,
    mediaType,
    handleChange,
    handleBlur,
    handleColorSelection,
    configOwnerId,
    configScope,
    updatedAt,
  } = props
  const [enabled, setEnabled] = useState<boolean>(false)
  const [errors, setErrors] = useState<ErrorsMap | undefined>()

  const confScope = configScope === "MARKET_AGENCY" ? "AGENCY" : configScope

  const [checkedLiDateRangeLimited, setCheckedLiDateRangeLimited] = useState(
    mediaType?.liDateRangeLimited ?? false,
  )

  // ** patch media type
  const [patch, { isLoading: isPatchLoading }] = useMutation(patchMediaType, {
    onSuccess: () =>
      queryCache.invalidateQueries([
        "global-media-types",
        { configScope: confScope, configOwnerId },
      ]),
    onError: (err: ApiError) => {
      const error = err as ApiError
      const errorMap = error?.errorsMap ?? {}
      setErrors(errorMap)
      queryCache.invalidateQueries([
        "global-media-types",
        { configScope: confScope, configOwnerId },
      ])
    },
  })

  const handleChangeSwitch = (event: React.ChangeEvent<HTMLInputElement>) => {
    patch({ id: mediaType?.id, liDateRangeLimited: event.target.checked })
    setCheckedLiDateRangeLimited(event.target.checked)
    setErrors(undefined)
  }

  const handleInput = (
    evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    handleChange(evt)
    setErrors(undefined)
  }

  const handleColor = (e: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => {
    handleColorSelection(e)
    setErrors(undefined)
  }

  const onToggle = () => {
    patch({ id: mediaType?.id, enableRateCard: !enabled })
    setEnabled(!enabled)
    setErrors(undefined)
  }

  const handleReset = () => {
    patch({ id: mediaType?.id, color: colorOptions[0] })
    setErrors(undefined)
  }

  const canISeeRateCard = configScope === "SUPPLIER"
  const canISeeStatus = !["AGENCY", "SUPPLIER", "SUPPLIER_GROUP"].includes(
    configScope || "",
  )

  useEffect(
    () => setEnabled(mediaType?.enableRateCard || false),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updatedAt, mediaType?.enableRateCard],
  )

  const gaps = mediaType?.activationGaps?.map((gap, index) => {
    return (
      <div style={{ marginBottom: index < 2 ? "5px" : undefined }}>
        {gap.toString()}
      </div>
    )
  })
  const tooltipTitle = <div>{gaps}</div>

  return (
    <>
      <div className={styles.container}>
        <TextInput
          name="semanticCode"
          label={t("admin.mediaTypes.semantic")}
          size="medium"
          margin="normal"
          value={mediaType?.semanticName ?? ""}
          disabled
        />
        <TextInput
          id={`${mediaType?.id}.name`}
          name="name"
          label={t("admin.mediaTypes.label")}
          size="medium"
          margin="normal"
          onChange={handleInput}
          onBlur={handleBlur}
          value={mediaType?.name}
          disabled={!hasAuthority(["AUTH_EDIT_MEDIA_TYPE"])}
        />
        <WithAuthority allowed={["AUTH_EDIT_MEDIA_TYPE"]}>
          <div className={styles.colors}>
            <label className="BodyBody-1BlackRegular">
              {t("admin.mediaTypes.color")}
            </label>
            <div className={styles.colorOptions}>
              {colorOptions.map((color, idx) => {
                return (
                  <button
                    key={idx}
                    type="button"
                    name="color"
                    id={`${mediaType?.id}.color`}
                    className={styles.trigger}
                    onClick={handleColor}
                    style={{ color }}
                  >
                    <div
                      className={cx("dot", {
                        selected:
                          (mediaType?.color?.toLowerCase() || "#48718c") ===
                          color.toLowerCase(),
                      })}
                    />
                  </button>
                )
              })}
            </div>
            <div>
              <ButtonBase
                className={styles.reset}
                onClick={handleReset}
                aria-label="clear selection"
              >
                {t("admin.mediaTypes.color.reset")}
              </ButtonBase>
            </div>
          </div>
        </WithAuthority>
        <WithAuthority allowed={["AUTH_VIEW_RATE_CARD"]}>
          {canISeeRateCard ? (
            <div className={styles.rateCard}>
              <div className={styles.rateCardContent}>
                <div
                  className={cx("BodyBody-1BlackRegular", styles.rateCardLabel)}
                >
                  {t("admin.mediaTypes.rateCard")}
                </div>
                <span className="BodyBody-sBlackRegular">
                  {t(
                    `admin.mediaTypes.rateCard.${
                      enabled ? "enabled" : "disabled"
                    }`,
                  )}
                </span>
                <Switch
                  color="primary"
                  checked={enabled}
                  onChange={onToggle}
                  disabled={!hasAuthority(["AUTH_EDIT_RATE_CARD"])}
                />
              </div>
              {!!errors?.enableRateCard && (
                <span className="BodyBody-xsRedRegular">
                  {t(errors?.enableRateCard)}
                </span>
              )}
            </div>
          ) : (
            <></>
          )}
        </WithAuthority>
        <WithAuthority>
          <>
            {canISeeStatus ? (
              <div className={styles.row}>
                <label className="BodyBody-1BlackRegular">Status</label>
                {mediaType?.status === "ACTIVE" ? (
                  <>
                    <span
                      className={cx("BodyBody-xsWhiteRegular", styles.active)}
                    >
                      {mediaType?.status}
                    </span>
                  </>
                ) : (
                  <>
                    <Tooltip
                      title={tooltipTitle}
                      classes={{
                        tooltip: cx("BodyBody-sWhiteRegular", styles.tooltip),
                      }}
                    >
                      <span
                        className={cx(
                          "BodyBody-xsWhiteRegular",
                          styles.inactive,
                        )}
                      >
                        {t("status.INACTIVE")}
                      </span>
                    </Tooltip>
                  </>
                )}
              </div>
            ) : null}
          </>
        </WithAuthority>
      </div>

      <div className={styles.limitLineItemContainer}>
        <label className="BodyBody-1BlackRegular">
          {t("market.admin.media.types.limit.month")}
        </label>
        <Switch
          size="small"
          color={
            isPatchLoading || !hasAuthority(["AUTH_ACCESS_MARKET_FINANCE"])
              ? undefined
              : "primary"
          }
          disabled={
            isPatchLoading || !hasAuthority(["AUTH_ACCESS_MARKET_FINANCE"])
          }
          className={cx(styles.switch)}
          checked={mediaType?.liDateRangeLimited ?? checkedLiDateRangeLimited}
          onChange={handleChangeSwitch}
        />
        <div className="BodyBody1Regular">{t("market.admin.toggle.on")}</div>
      </div>
    </>
  )
}

const styles = {
  container: css`
    display: grid;
    align-items: center;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 2fr 1fr;
    grid-gap: var(--spacing-l);
    margin-top: var(--spacing-m);
  `,
  trigger: css`
    display: inline-flex;
    position: relative;
    align-items: center;
    justify-content: center;
    margin: 0;
    padding: var(--spacing-xs);
    border: 0;
    outline: 0;
    color: inherit;
    background-color: transparent;
    user-select: none;
    cursor: pointer;

    :disabled {
      cursor: default;
    }

    :focus:enabled {
      .dot {
        box-shadow:
          0 0 0 2px #fff,
          0 0 0 4px currentColor;
      }
    }
    :hover:enabled {
      .dot {
        box-shadow:
          0 0 0 2px #fff,
          0 0 0 4px currentColor;
      }
    }

    .dot {
      width: 16px;
      height: 16px;
      line-height: 1rem;
      border-radius: 50%;
      background-color: currentColor;
      transition: box-shadow 120ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    }

    .selected {
      box-shadow:
        0 0 0 2px #fff,
        0 0 0 4px currentColor;
    }
  `,
  colors: css`
    background-color: #fff;
    display: flex;
    align-items: center;
    width: 100%;
    grid-column-start: 1;
    grid-column-end: 3;

    > :not(:last-child) {
      margin-right: 1.5rem;
    }
  `,
  colorOptions: css`
    flex: 1;
    display: inline-flex;
    align-items: center;
  `,
  reset: css`
    border-radius: 4px;
    padding: 4px 14px;
    font-size: 0.875rem;
    line-height: 1rem;
    color: #00b1a4;
    transition:
      background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
      box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
      border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    :focus:enabled {
      background-color: rgba(0, 177, 163, 0.04);
    }
    :hover:enabled {
      background-color: rgba(0, 177, 163, 0.04);
    }
    white-space: nowrap;
  `,
  rateCard: css`
    display: flex;
    flex-direction: column;
  `,
  rateCardContent: css`
    display: flex;
    align-items: center;
  `,
  rateCardLabel: css`
    min-width: 100px;
  `,
  row: css`
    display: flex;
    flex-direction: row;
  `,
  active: css`
    background-color: var(--primary-main);
    padding: 2px var(--spacing-xs) 0;
    margin: 1px 0 0 var(--spacing-xl);
    max-height: var(--spacing-m);
  `,
  inactive: css`
    background-color: var(--red);
    padding: 2px var(--spacing-xs) 0px;
    margin: 1px 0 0 var(--spacing-xl);
    max-height: var(--spacing-m);
    text-transform: uppercase;
    box-shadow: 0 2px 4px 0 var(--light-grey);
  `,
  tooltip: css`
    background-color: var(--red);
    border-radius: 2px;
  `,
  limitLineItemContainer: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-top: var(--spacing-l);
  `,
  switch: css`
    margin-left: 5px;
  `,
}
